import { ReactNode, useEffect, useRef } from 'react';
import { format, isAfter, isSameDay } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { Container, Dropdown } from 'semantic-ui-react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { useProjectContext, usePortfolioContext } from '~/hooks';
import { usePermissions } from '~/requests/permissions/usePermissions';
import { DealStatusBadge } from '~/components/DealStatusBadge';
import { DealStates, ProjectStatus } from '~/constants';
import NavigationTabs, { NavigationTabConfig } from './NavigationTabs';
import EditableName from './EditableName';
import { usePageHeaderContext } from './PageHeaderProvider';
import { savePortfolioNavScroll } from '~/utils/portfolioNavScroll';
import './styles.scss';

// Portfolio has no worksheet tab; use review when coming from project worksheet
const PORTFOLIO_TABS = ['review', 'bidding', 'messages', 'dataroom', 'team', 'listing'];

interface PageHeaderProps {
  portfolio?: any
  project?: any
  navTabs: NavigationTabConfig[]
  content?: ReactNode
  secondaryNavContent?: ReactNode
}

export default function PageHeader({
  project,
  portfolio,
  content,
  navTabs,
  secondaryNavContent,
}: PageHeaderProps) {

  const { projectState, projectDisplayValues } = useProjectContext();
  const { portfolioState, portfolioDisplayValues } = usePortfolioContext();
  const { permissions } = usePermissions();
  const history = useHistory();
  const { pathname, search } = useLocation();

  // Derive current tab from path (e.g. /project/123/bidding -> 'bidding')
  const pathSegments = pathname.split('/').filter(Boolean);
  const currentTab = pathSegments[2]; // segment after /project|portfolio/:id/
  const portfolioTab = currentTab && PORTFOLIO_TABS.includes(currentTab) ? currentTab : 'review';
  const projectTab = currentTab || 'review';

  const type = portfolio ? 'portfolio' : project?.portfolioId ? 'portfolioProject' : 'project';

  // Create dropdown options for portfolio navigation
  const createDropdownOptions = () => {
    // Investors should not see projects they're no longer in the running for — their data rooms
    // are blocked server-side (see verifyDataroomAccess), so the nav entry would just 404/403.
    const isInvestorView = !permissions?.isDeveloper && !permissions?.isAdmin;
    const hideDeclined = (list: any[]) =>
      isInvestorView
        ? list.filter((p: any) => p.status !== ProjectStatus.DECLINED_TO_BID && p.status !== ProjectStatus.BID_REJECTED)
        : list;

    if (type === 'portfolio' && portfolio?.projects) {

      // Add individual projects
      let projects: any;
      if ((permissions?.isDeveloper || permissions?.isAdmin) && portfolioState.latestState < DealStates.IN_DILIGENCE) {
        projects = portfolio.projects.filter((p: any) => !p.parentId);
      } else {
        projects = hideDeclined(portfolio.projects);
      }

      const options = [
        {
          key: 'portfolio',
          text: `Portfolio Overview (${projects.length} ${projects.length === 1 ? 'project' : 'projects'})`,
          value: 'portfolio',
          // icon: 'stack'
        }
      ];

      projects.forEach((p: any) => {
        options.push({
          key: p.id,
          text: p.name || `Project ${p.id}`,
          value: p.id,
          // icon: 'lightning'
        });
      });
      if (options.length > 1) {
        const [first, ...rest] = options;
        rest.sort((a: any, b: any) => {
          const aText = String(a?.text ?? '');
          const bText = String(b?.text ?? '');
          return aText.localeCompare(bText);
        });
        return [first, ...rest];
      }

      return options;
    } else if (type === 'portfolioProject' && project?.portfolio?.projects) {
      // Add individual projects
      let projects: any;
      if ((permissions?.isDeveloper || permissions?.isAdmin) && projectState.dealState < DealStates.IN_DILIGENCE) {
        projects = project.portfolio.projects.filter((p: any) => !p.parentId);
      } else {
        projects = hideDeclined(project.portfolio.projects);
      }

      const options = [
        {
          key: 'portfolio',
          text: `Portfolio Overview (${projects.length} ${projects.length === 1 ? 'project' : 'projects'})`,
          value: 'portfolio',
          // icon: 'stack'
        }
      ];

      projects.forEach((p: any) => {
        options.push({
          key: p.id,
          text: p.name || `Project ${p.id}`,
          value: p.id,
          // icon: 'lightning'
        });
      });
      // Sort options (except the portfolio overview) by text, keeping the first option at the top
      if (options.length > 1) {
        const [first, ...rest] = options;
        rest.sort((a: any, b: any) => {
          const aText = String(a?.text ?? '');
          const bText = String(b?.text ?? '');
          return aText.localeCompare(bText);
        });
        return [first, ...rest];
      }
      return options;
    }

    return [];
  };

  const handleNavigation = (event: any, { value }: any) => {
    if (value === 'portfolio') {
      if (type === 'portfolio') {
        // Already on portfolio, do nothing
        return;
      } else if (type === 'portfolioProject') {
        savePortfolioNavScroll();
        history.push(`/portfolio/${project.portfolio.id}/${portfolioTab}${search || ''}`);
      }
    } else {
      // Navigate to specific project, preserving current tab and URL params (e.g. worksheet s param)
      savePortfolioNavScroll();
      history.push(`/project/${value}/${projectTab}${search || ''}`);
    }
  };

  const getCurrentValue = () => {
    if (type === 'portfolio') return 'portfolio';
    if (type === 'portfolioProject') return project.id;
    return null;
  };

  const dropdownOptions = createDropdownOptions();

  // Mini header visibility: show when the main header has scrolled above the viewport.
  const mainHeaderRef = useRef<HTMLDivElement | null>(null);
  const miniHeaderRef = useRef<HTMLDivElement | null>(null);
  const { miniHeaderVisible: showMiniHeader, setMiniHeaderVisible, setHeight } = usePageHeaderContext();
  useEffect(() => {
    const el = mainHeaderRef.current;
    if (!el) return;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setMiniHeaderVisible(rect.bottom < 0);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      setMiniHeaderVisible(false);
    };
  }, [setMiniHeaderVisible]);

  // Track mini-header height so downstream sticky elements (worksheet action
  // bar, section headings) can dock right below it.
  useEffect(() => {
    const el = miniHeaderRef.current;
    if (!el) return;
    const update = () => setHeight(el.getBoundingClientRect().height);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      ro.disconnect();
      setHeight(0);
    };
  }, [setHeight]);

  useEffect(() => {
    document.body.classList.toggle('mini-page-header-visible', showMiniHeader);
    return () => document.body.classList.remove('mini-page-header-visible');
  }, [showMiniHeader]);

  let totalPv = 0;
  let totalStorageDischarge = 0;
  let totalStorageWattage = 0;
  if (project) {
    totalPv = +((
      project.projectArrays?.reduce((sum: number, array: any) => sum + (array.sizeKwdc || 0), 0) / 1000
    )?.toFixed(3) || 0);
    totalStorageDischarge = +((
      project.storage?.reduce((sum: number, storage: any) => sum + (storage.discharge || 0), 0) / 1000
    )?.toFixed(3) || 0);
    totalStorageWattage = +((
      project.storage?.reduce((sum: number, storage: any) => sum + (storage.wattage || 0), 0) / 1000
    )?.toFixed(3) || 0);
  } else {
    totalPv = +((
      portfolio?.projects
        ?.filter((p: any) => portfolioState.latestState < DealStates.IN_DILIGENCE ? (
          permissions?.isInvestor ? true : !p.parentId
        ) : (
          true
        ))
        .reduce(
          (sum: number, p: any) =>
            sum +
            (p.projectArrays?.reduce(
              (subSum: number, array: any) => subSum + (array.sizeKwdc || 0),
              0
            ) || 0),
          0
        ) / 1000
    )?.toFixed(3) || 0);
    totalStorageDischarge = +((
      portfolio?.projects
        ?.filter((p: any) => portfolioState.latestState < DealStates.IN_DILIGENCE ? (
          permissions?.isInvestor ? true : !p.parentId
        ) : (
          true
        ))
        .reduce(
          (sum: number, p: any) =>
            sum +
            (p.storage?.reduce(
              (subSum: number, storage: any) => subSum + (storage.discharge || 0),
              0
            ) || 0),
          0
        ) / 1000
    )?.toFixed(3) || 0);
    totalStorageWattage = +((
      portfolio?.projects
        ?.filter((p: any) => portfolioState.latestState < DealStates.IN_DILIGENCE ? (
          permissions?.isInvestor ? true : !p.parentId
        ) : (
          true
        ))
        .reduce(
          (sum: number, p: any) =>
            sum +
            (p.storage?.reduce(
              (subSum: number, storage: any) => subSum + (storage.wattage || 0),
              0
            ) || 0),
          0
        ) / 1000
    )?.toFixed(3) || 0);
  }

  const sellerEntity = portfolio ?? project;
  const sellerName = sellerEntity?.installer?.company?.name;
  const sellerCompanyId =
    sellerEntity?.installer?.company?.id ?? sellerEntity?.installer?.companyId;

  const bidDeadline = project
    ? (project?.bidDeadline || project?.portfolio?.bidDeadline)
    : portfolio?.bidDeadline;
  const zonedBidDeadline = bidDeadline ? utcToZonedTime(bidDeadline, 'UTC') : null;
  const formattedBidDeadline = zonedBidDeadline ? format(zonedBidDeadline, 'MM/dd/yyyy') : null;
  // Passed only once the current date is strictly after the deadline; a deadline
  // on the current date is not passed.
  const bidDeadlinePassed = !!zonedBidDeadline
    && !isSameDay(utcToZonedTime(new Date(), 'UTC'), zonedBidDeadline)
    && isAfter(utcToZonedTime(new Date(), 'UTC'), zonedBidDeadline);

  return (
    <div>
      <div
        ref={miniHeaderRef}
        className={`mini-page-header ${type === 'portfolio' ? 'portfolio-page-header' : 'project-page-header'} ${showMiniHeader ? 'visible' : ''}`}
        aria-hidden={!showMiniHeader}
      >
        <Container className="mini-page-header__inner">
          {dropdownOptions.length > 0 ? (
            <Dropdown
              inline
              options={dropdownOptions}
              value={getCurrentValue()}
              onChange={handleNavigation}
              className="portfolio-nav-dropdown"
            />
          ) : (
            <span className="mini-page-header__label">
              {project?.name || portfolio?.name}
            </span>
          )}
          <DealStatusBadge status={project ? projectDisplayValues?.status : portfolioDisplayValues?.status} />
        </Container>
      </div>
      <div
        ref={mainHeaderRef}
        className={`header-container card ${type === 'portfolio' ? 'portfolio-page-header' : 'project-page-header'}`}
      >
        <div className="breadcrumbs">{project ? 'Project Details' : 'Portfolio'}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <EditableName type={type} />
          <DealStatusBadge status={project ? projectDisplayValues?.status : portfolioDisplayValues?.status} />
        </div>
        <div className="header-data-bar">
          <div className="data-item">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-stack" viewBox="0 0 16 16">
              <path d="m14.12 10.163 1.715.858c.22.11.22.424 0 .534L8.267 15.34a.6.6 0 0 1-.534 0L.165 11.555a.299.299 0 0 1 0-.534l1.716-.858 5.317 2.659c.505.252 1.1.252 1.604 0l5.317-2.66zM7.733.063a.6.6 0 0 1 .534 0l7.568 3.784a.3.3 0 0 1 0 .535L8.267 8.165a.6.6 0 0 1-.534 0L.165 4.382a.299.299 0 0 1 0-.535z" />
              <path d="m14.12 6.576 1.715.858c.22.11.22.424 0 .534l-7.568 3.784a.6.6 0 0 1-.534 0L.165 7.968a.299.299 0 0 1 0-.534l1.716-.858 5.317 2.659c.505.252 1.1.252 1.604 0z" />
            </svg>
            <div>
              {dropdownOptions.length > 0 ? (
                <Dropdown
                  inline
                  options={dropdownOptions}
                  value={getCurrentValue()}
                  onChange={handleNavigation}
                  className="portfolio-nav-dropdown"
                />
              ) : (
                project?.portfolio ?
                  `${project.portfolio.name || 'Portfolio'} (${project.portfolio.projects?.length || 0} projects)` :
                  projectDisplayValues?.status === 'Estimate' ? 'Quick Estimate' : 'Single Project'
              )}
            </div>
          </div>
          {totalPv > 0 && (
            <div className="data-item">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-lightning-fill" viewBox="0 0 16 16">
                <path d="M5.52.359A.5.5 0 0 1 6 0h4a.5.5 0 0 1 .474.658L8.694 6H12.5a.5.5 0 0 1 .395.807l-7 9a.5.5 0 0 1-.873-.454L6.823 9.5H3.5a.5.5 0 0 1-.48-.641z" />
              </svg>
              <div className="label">PV: {totalPv} MWdc</div>
            </div>
          )}
          {totalStorageWattage > 0 && (
            <div className="data-item">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-battery-full" viewBox="0 0 16 16">
                <path d="M2 6h10v4H2z" />
                <path d="M2 4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm10 1a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1zm4 3a1.5 1.5 0 0 1-1.5 1.5v-3A1.5 1.5 0 0 1 16 8" />
              </svg>
              <div className="label">BESS: {totalStorageWattage} MW, {totalStorageDischarge} MWh</div>
            </div>
          )
          }
          <div className="data-item">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-person-fill" viewBox="0 0 16 16">
              <path d="M3 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6" />
            </svg>
            <div className="label">
              Seller:{' '}
              {permissions?.isAdmin && sellerCompanyId && sellerName ? (
                <Link to={`/company-profile/${sellerCompanyId}`} className="page-header-seller-link">
                  {sellerName}
                </Link>
              ) : (
                sellerName
              )}
            </div>
          </div>
          {formattedBidDeadline && (
            <div className="data-item">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-calendar-event" viewBox="0 0 16 16">
                <path d="M11 6.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5z" />
                <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5M1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4z" />
              </svg>
              <div className="label">
                Bid deadline:{' '}
                {bidDeadlinePassed ? (
                  <span style={{ color: 'var(--color-red)' }}>{formattedBidDeadline}</span>
                ) : (
                  formattedBidDeadline
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* {content && <div>{content}</div>} */}
      <div className="navigation-tabs-container card">
        <NavigationTabs tabs={navTabs} />
        {secondaryNavContent && (
          <div className="secondary-navigation-content">
            {secondaryNavContent}
          </div>
        )}
      </div>
    </div>
  );
}
